/**
 * Connecticut State-Specific Diagnostic Questions
 * All questions reference actual CT regulations, forms, and requirements
 * 
 * Regulatory Body: Connecticut Department of Public Health (DPH)
 * Primary Regulation: Conn. Agencies Regs. 19-13-D66 through 19-13-D79
 * 
 * REGULATORY STRUCTURE:
 * - 19-13-D66: Definitions
 * - 19-13-D67: Personnel
 * - 19-13-D68: General requirements
 * - 19-13-D69: Services (includes ALL training requirements at subsection d)
 * - 19-13-D70: Contracted services
 * - 19-13-D71: Personnel policies
 * - 19-13-D72: Patient care policies
 * - 19-13-D73: Patient care plan
 * - 19-13-D74: Administration of medicines
 * - 19-13-D75: Clinical record system
 * - 19-13-D76: Quality assurance program
 * - 19-13-D77: Administrative organization and records
 * - 19-13-D78: Patient's bill of rights and responsibilities
 * - 19-13-D79: Facilities
 * 
 * KEY STATUTES:
 * - CGS 19a-491c: Criminal history and patient abuse background search (ABCMS)
 * - CGS 19a-491d: Comprehensive background check requirements
 * - CGS 20-670 through 20-682: Homemaker-Companion Agency (DCP registration)
 * - CGS 17b-451: Protective Services for the Elderly (mandatory reporting)
 * 
 * TRAINING REQUIREMENTS (19-13-D69(d)(2)):
 * - 75 hours initial training
 * - 12 hours annual in-service
 * - 10 hours orientation before patient contact
 * 
 * RN STAFFING RATIO (19-13-D69(D)):
 * - 1 full-time RN for every 15 FTE homemaker-home health aides
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const CONNECTICUT_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'CT-A1',
    itemId: 'A1',
    stateCode: 'CT',
    title: 'DPH Home Health Care Agency License Status',
    description: 'Connecticut requires all home health care agencies to be licensed by DPH',
    severity: 'high',
    regulationCode: 'CGS 19a-490',
    regulationName: 'Licensing',
    agencyName: 'Connecticut Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(860) 509-7400',
    agencyUrl: 'https://portal.ct.gov/DPH/Facility-Licensing--Investigations/Facility-Licensing',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Health Care Agency license from Connecticut DPH?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Home health care agency licenses are renewed through the CT eLicense portal.',
      },
      {
        id: 'q2',
        question: 'What is your home health care agency license expiration date?',
        inputType: 'date',
        helpText: 'Found on your DPH license certificate or CT eLicense portal',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 120, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 60, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your DPH Home Health Care Agency license is current and active per CGS 19a-490.',
      atRisk: 'Your license expires within 120 days. Submit renewal through CT eLicense portal before expiration.',
      nonCompliant: 'Operating a home health care agency in Connecticut without a valid DPH license violates CGS 19a-490. Contact DPH at (860) 509-7400.',
      unknown: 'Verify your license status at elicense.ct.gov or contact DPH at (860) 509-7400.',
    },
    stateNotes: [
      'License renewals are processed through CT eLicense portal (elicense.ct.gov)',
      'DPH conducts periodic inspections and complaint investigations',
      'Medicare-certified agencies must also comply with 42 CFR Part 484',
    ],
  },

  {
    id: 'CT-A2',
    itemId: 'A2',
    stateCode: 'CT',
    title: 'Homemaker-Companion Agency Registration',
    description: 'Connecticut requires DCP registration for homemaker-companion services under CGS 20-670',
    severity: 'high',
    regulationCode: 'CGS 20-670',
    regulationName: 'Registration',
    agencyName: 'Connecticut Department of Consumer Protection',
    agencyAbbr: 'DCP',
    agencyPhone: '(860) 713-6100',
    agencyUrl: 'https://portal.ct.gov/DCP/License-Services-Division/License-Division/Homemaker-Companion-Agency-Registration',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide homemaker or companion services (non-medical, supportive services)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - skilled care only' },
        ],
        required: true,
        helpText: 'Homemaker-companion services are non-medical services like cooking, cleaning, personal hygiene assistance',
      },
      {
        id: 'q2',
        question: 'Is your agency registered with the Department of Consumer Protection as a Homemaker-Companion Agency?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, current DCP registration' },
          { value: 'separate_entity', label: 'Yes, through a separate registered entity' },
          { value: 'no', label: 'No DCP registration' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'DPH-licensed agencies must form a separate entity for homemaker-companion services and register with DCP',
      },
      {
        id: 'q3',
        question: 'Do you maintain the required $10,000 surety bond for homemaker-companion services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q2',
          operator: 'includes',
          value: ['yes', 'separate_entity'],
        },
        helpText: 'CGS 20-672(a)(3) requires minimum $10,000 surety bond covering employee theft',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q2', value: ['yes', 'separate_entity'] },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your homemaker-companion registration status meets CGS 20-670 requirements.',
      atRisk: 'Review your homemaker-companion registration and surety bond with DCP.',
      nonCompliant: 'Operating homemaker-companion services without DCP registration violates CGS 20-671. Contact DCP at (860) 713-6100. Registration requires $10,000 surety bond per CGS 20-672(a)(3).',
      unknown: 'Verify homemaker-companion registration requirements with DCP.',
    },
    stateNotes: [
      'DPH-licensed home health agencies providing non-medical homemaker-companion services must form separate entity',
      'Registration expires October 31 each year',
      'Registration fee is $375',
      '$10,000 surety bond required covering employee theft per CGS 20-672(a)(3)',
      'Comprehensive background check required for all employees per CGS 20-678',
    ],
  },

  {
    id: 'CT-A3',
    itemId: 'A3',
    stateCode: 'CT',
    title: 'Administrator Qualifications',
    description: 'Connecticut requires qualified administrators for home health agencies per 19-13-D67',
    severity: 'high',
    regulationCode: '19-13-D67',
    regulationName: 'Administrator',
    agencyName: 'Connecticut Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(860) 509-7400',
    agencyUrl: 'https://portal.ct.gov/DPH',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a designated administrator who meets DPH qualification requirements?',
        inputType: 'checklist',
        options: [
          { value: 'designated', label: 'Designated administrator in place' },
          { value: 'experience', label: 'Minimum healthcare management experience' },
          { value: 'education', label: 'Required education or equivalent experience' },
          { value: 'fulltime', label: 'Available full-time during agency operation' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Agency administrator meets Connecticut qualification requirements per 19-13-D67.',
      atRisk: 'Administrator qualifications may need enhancement. Review requirements with DPH.',
      nonCompliant: 'Administrator does not meet Connecticut qualification requirements per 19-13-D67. Contact DPH at (860) 509-7400.',
      unknown: 'Verify administrator qualifications with DPH requirements.',
    },
    stateNotes: [
      'Administrator qualifications defined in 19-13-D67',
      'Must have healthcare management experience',
      'Must be available full-time during agency operating hours',
      'Responsible for overall agency operations and compliance',
      'Changes in administrator must be reported to DPH',
    ],
  },

  {
    id: 'CT-A4',
    itemId: 'A4',
    stateCode: 'CT',
    title: 'RN Staffing Ratio Requirements',
    description: 'Connecticut requires specific RN-to-aide staffing ratios per 19-13-D69(D)',
    severity: 'high',
    regulationCode: '19-13-D69',
    regulationName: 'RN Staffing',
    agencyName: 'Connecticut Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(860) 509-7400',
    agencyUrl: 'https://portal.ct.gov/DPH',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency employ or contract with a licensed RN for clinical supervision?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you maintain the required RN staffing ratio of 1 full-time RN for every 15 FTE homemaker-home health aides?',
        inputType: 'select',
        options: [
          { value: 'meets', label: 'Yes, we meet or exceed 1:15 ratio' },
          { value: 'close', label: 'Close to ratio but may exceed at times' },
          { value: 'exceeds', label: 'No, we have more than 15 aides per RN' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: '19-13-D69(D) requires 1 full-time RN for every 15 or fewer FTE homemaker-home health aides on duty',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'meets', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'close', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'exceeds', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'RN staffing ratio meets Connecticut requirements per 19-13-D69(D).',
      atRisk: 'RN staffing ratio may not consistently meet 1:15 requirement. Monitor closely.',
      nonCompliant: 'RN staffing is inadequate. 19-13-D69(D) requires 1 full-time RN for every 15 FTE aides. Hire additional RN staff immediately.',
      unknown: 'Verify RN staffing requirements with DPH.',
    },
    stateNotes: [
      'Ratio is 1 full-time RN per 15 or fewer FTE homemaker-home health aides on duty',
      'This is during regular workweek hours',
      'RN must be available for supervision and patient assessments',
    ],
  },

  {
    id: 'CT-A5',
    itemId: 'A5',
    stateCode: 'CT',
    title: 'RN Supervisory Visits',
    description: 'Connecticut requires RN supervisory visits every 60 days per 19-13-D69',
    severity: 'high',
    regulationCode: '19-13-D69',
    regulationName: 'RN Supervision',
    agencyName: 'Connecticut Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(860) 509-7400',
    agencyUrl: 'https://portal.ct.gov/DPH',
    questions: [
      {
        id: 'q1',
        question: 'How often does an RN conduct supervisory visits with each patient receiving aide services?',
        inputType: 'select',
        options: [
          { value: 'every_60', label: 'At least every 60 days (meets requirement)' },
          { value: 'every_90', label: 'Every 90 days' },
          { value: 'quarterly', label: 'Quarterly or less frequently' },
          { value: 'none', label: 'No scheduled supervisory visits' },
        ],
        required: true,
        helpText: '19-13-D69 requires RN assessment not less than every 60 days while aide is providing services',
      },
      {
        id: 'q2',
        question: 'Are supervisory visits conducted while the aide is providing services in the patient\'s home?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, RN observes aide during visit' },
          { value: 'no', label: 'No, separate from aide visits' },
        ],
        helpText: '19-13-D69 specifies assessment shall be completed while aide is providing services',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'every_60' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'every_90', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['quarterly', 'none'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'RN supervisory visits meet Connecticut requirements - every 60 days while aide is present per 19-13-D69.',
      atRisk: 'RN supervisory visits should occur at least every 60 days. Consider increasing frequency.',
      nonCompliant: 'RN supervision is inadequate. 19-13-D69 requires visits not less than every 60 days. Implement proper RN supervision immediately.',
      unknown: 'Verify RN supervision requirements with DPH.',
    },
    stateNotes: [
      'RN assessment required not less than every 60 days per 19-13-D69',
      'Assessment must be completed while aide is providing services',
      'RN must observe aide performance during visit',
      'Documentation of supervisory visit required in patient record',
      'More frequent visits may be required based on patient acuity',
    ],
  },

  {
    id: 'CT-A6',
    itemId: 'A6',
    stateCode: 'CT',
    title: 'Written Policies and Procedures',
    description: 'Connecticut requires specific written policies per 19-13-D71 and 19-13-D72',
    severity: 'medium',
    regulationCode: '19-13-D71',
    regulationName: 'Policies',
    agencyName: 'Connecticut Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(860) 509-7400',
    agencyUrl: 'https://portal.ct.gov/DPH',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following Connecticut-required policies does your agency have documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'orientation', label: 'Orientation policy and procedure (19-13-D71)' },
          { value: 'health_exam', label: 'Physical examination including TB test (19-13-D71(a)(5))' },
          { value: 'job_descriptions', label: 'Written job descriptions (19-13-D71)' },
          { value: 'personnel_records', label: 'Personnel records maintenance (19-13-D71)' },
          { value: 'patient_rights', label: 'Patient bill of rights (19-13-D78)' },
          { value: 'care_plan', label: 'Patient care plan requirements (19-13-D73)' },
          { value: 'discharge', label: 'Discharge categories and procedures (19-13-D72)' },
          { value: 'complaint', label: 'Complaint and grievance procedures' },
          { value: 'infection', label: 'Infection control policies' },
          { value: 'quality', label: 'Quality assurance program (19-13-D76)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policy manual meets Connecticut 19-13-D71 and 19-13-D72 requirements.',
      atRisk: 'Your policy manual is missing required sections. DPH surveyors will review policies for completeness. Missing: {missing}.',
      nonCompliant: 'Multiple required policies are missing. This is a common DPH survey deficiency. Immediately develop: {missing}.',
      unknown: 'Review your policies against 19-13-D71 and 19-13-D72 requirements.',
    },
    stateNotes: [
      'Personnel policies required per 19-13-D71 including orientation and job descriptions',
      'Patient care policies required per 19-13-D72 including discharge procedures',
      'Patient Bill of Rights required per 19-13-D78',
      'Quality assurance program required per 19-13-D76',
      'Policies must be reviewed and updated at least annually',
    ],
  },

  {
    id: 'CT-A7',
    itemId: 'A7',
    stateCode: 'CT',
    title: 'Quality Assurance Program',
    description: 'Connecticut requires a documented quality assurance program per 19-13-D76',
    severity: 'medium',
    regulationCode: '19-13-D76',
    regulationName: 'Quality Assurance',
    agencyName: 'Connecticut Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(860) 509-7400',
    agencyUrl: 'https://portal.ct.gov/DPH',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a documented quality assurance program?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which quality assurance activities does your program include?',
        inputType: 'checklist',
        options: [
          { value: 'chart_review', label: 'Regular patient chart reviews' },
          { value: 'satisfaction', label: 'Patient satisfaction surveys' },
          { value: 'incident', label: 'Incident tracking and analysis' },
          { value: 'corrective', label: 'Corrective action procedures' },
          { value: 'outcomes', label: 'Patient outcome monitoring' },
          { value: 'staff_eval', label: 'Staff performance evaluation' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Quality assurance program meets Connecticut requirements per 19-13-D76.',
      atRisk: 'Quality assurance program is incomplete. Add additional components per 19-13-D76.',
      nonCompliant: 'No quality assurance program exists. This violates 19-13-D76. Develop a QA program immediately.',
      unknown: 'Review quality assurance requirements per 19-13-D76.',
    },
    stateNotes: [
      'Quality assurance program required per 19-13-D76',
      'Must include regular patient chart reviews and outcome monitoring',
      'Patient satisfaction surveys recommended',
      'Incident tracking and corrective action procedures required',
      'QA findings must be documented and used for program improvement',
    ],
  },

  {
    id: 'CT-A8',
    itemId: 'A8',
    stateCode: 'CT',
    title: 'Patient Rights Notification',
    description: 'Connecticut requires agencies to inform patients of their rights per 19-13-D78',
    severity: 'high',
    regulationCode: '19-13-D78',
    regulationName: 'Patient Rights',
    agencyName: 'Connecticut Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(860) 509-7400',
    agencyUrl: 'https://portal.ct.gov/DPH',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide all patients with a written Patient Bill of Rights?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, before services begin' },
          { value: 'sometimes', label: 'Sometimes, not consistently' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your Patient Bill of Rights include all Connecticut-required elements?',
        inputType: 'checklist',
        options: [
          { value: 'respect', label: 'Right to be treated with dignity and respect' },
          { value: 'privacy', label: 'Right to privacy and confidentiality' },
          { value: 'participate', label: 'Right to participate in care planning' },
          { value: 'refuse', label: 'Right to refuse treatment' },
          { value: 'complaint', label: 'Right to voice complaints without retaliation' },
          { value: 'dph_contact', label: 'DPH contact information for complaints' },
          { value: 'info', label: 'Right to be informed about services and charges' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'always',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 6 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'sometimes', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Patient rights notification meets Connecticut requirements per 19-13-D78.',
      atRisk: 'Patient Bill of Rights delivery is inconsistent. Ensure ALL patients receive it before services begin.',
      nonCompliant: 'Patients are not receiving required Patient Bill of Rights. This violates 19-13-D78. Implement immediately.',
      unknown: 'Verify patient rights notification requirements per 19-13-D78.',
    },
    stateNotes: [
      'Patient Bill of Rights required per 19-13-D78',
      'Must be provided before services begin',
      'Must include DPH contact information for complaints',
      'Patient must acknowledge receipt in writing',
      'Rights include dignity, privacy, participation in care, and right to refuse treatment',
    ],
  },

  {
    id: 'CT-A9',
    itemId: 'A9',
    stateCode: 'CT',
    title: 'Clinical Record System',
    description: 'Connecticut requires comprehensive clinical record system per 19-13-D75',
    severity: 'high',
    regulationCode: '19-13-D75',
    regulationName: 'Clinical Records',
    agencyName: 'Connecticut Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(860) 509-7400',
    agencyUrl: 'https://portal.ct.gov/DPH',
    questions: [
      {
        id: 'q1',
        question: 'Do patient clinical records contain all Connecticut-required elements?',
        inputType: 'checklist',
        options: [
          { value: 'demographics', label: 'Patient demographics and contact information' },
          { value: 'emergency', label: 'Emergency contact information' },
          { value: 'physician', label: 'Physician orders or prescriptions' },
          { value: 'care_plan', label: 'Written care plan (19-13-D73)' },
          { value: 'assessments', label: 'RN assessments' },
          { value: 'visit_notes', label: 'Visit documentation and progress notes' },
          { value: 'consent', label: 'Signed consent forms' },
          { value: 'medications', label: 'Medication records (if applicable)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical records contain all Connecticut-required elements per 19-13-D75.',
      atRisk: 'Clinical records may be incomplete. Review against 19-13-D75 requirements.',
      nonCompliant: 'Clinical records are missing required elements. Update documentation immediately.',
      unknown: 'Review clinical record requirements per 19-13-D75.',
    },
    stateNotes: [
      'Clinical record system requirements defined in 19-13-D75',
      'Must include patient demographics, emergency contacts, and care plan',
      'Physician orders must be documented and signed',
      'Visit notes and progress notes required for each service',
      'Records must be secured against unauthorized access',
    ],
  },

  {
    id: 'CT-A10',
    itemId: 'A10',
    stateCode: 'CT',
    title: 'Record Retention Requirements',
    description: 'Connecticut requires minimum record retention periods',
    severity: 'medium',
    regulationCode: '19-13-D75',
    regulationName: 'Record Retention',
    agencyName: 'Connecticut Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(860) 509-7400',
    agencyUrl: 'https://portal.ct.gov/DPH',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain patient and employee records?',
        inputType: 'select',
        options: [
          { value: '7plus', label: '7 years or longer' },
          { value: '5to6', label: '5-6 years' },
          { value: 'less5', label: 'Less than 5 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: '7plus', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: '5to6', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['less5', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your record retention policy meets Connecticut requirements.',
      atRisk: 'Connecticut recommends 7-year record retention. Update your policy to ensure compliance.',
      nonCompliant: 'Your record retention does not meet Connecticut requirements. Immediately update your retention policy.',
      unknown: 'Review record retention requirements.',
    },
    stateNotes: [
      'Connecticut recommends 7-year retention for patient and employee records',
      'Medicare requires 5-year retention for certified agencies',
      'For minors, retain until age of majority plus standard retention period',
      'Electronic records must meet same retention requirements',
      'Must have procedures for record retention if agency ceases operation',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL FILES
  // ============================================
  {
    id: 'CT-B1',
    itemId: 'B1',
    stateCode: 'CT',
    title: 'Personnel File Requirements',
    description: 'Connecticut requires specific documents in each employee\'s personnel file per 19-13-D71',
    severity: 'high',
    regulationCode: '19-13-D71',
    regulationName: 'Personnel Files',
    agencyName: 'Connecticut Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(860) 509-7400',
    agencyUrl: 'https://portal.ct.gov/DPH',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following documents do you maintain in EVERY home health aide\'s personnel file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'app', label: 'Employment application' },
          { value: 'id', label: 'Government-issued photo ID' },
          { value: 'background', label: 'Criminal background check results (ABCMS)' },
          { value: 'registry', label: 'Nurse Aide Registry verification' },
          { value: 'references', label: 'Professional references verified' },
          { value: 'tb', label: 'TB test and communicable disease clearance (19-13-D71(a)(5))' },
          { value: 'physical', label: 'Physical examination documentation' },
          { value: 'i9', label: 'I-9 Employment Eligibility Verification' },
          { value: 'training', label: 'Training documentation (75 hours)' },
          { value: 'competency', label: 'Competency evaluation records' },
          { value: 'job_desc', label: 'Signed job description' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain all Connecticut-required documentation per 19-13-D71.',
      atRisk: 'Some personnel files are missing required documents. Connecticut requires: ABCMS background check, TB clearance, and 75-hour training records. Missing: {missing}.',
      nonCompliant: 'Personnel files are missing critical documentation required by 19-13-D71. DPH surveyors will cite incomplete files. Immediately obtain: {missing}.',
      unknown: 'Audit personnel files against 19-13-D71 requirements.',
    },
    stateNotes: [
      'Personnel records required per 19-13-D71',
      'ABCMS background check required per CGS 19a-491c',
      'Physical examination including TB test required per 19-13-D71(a)(5)',
      '75-hour training documentation required per 19-13-D69(d)(2)',
      'Signed job description must be on file for each employee',
    ],
  },

  {
    id: 'CT-B2',
    itemId: 'B2',
    stateCode: 'CT',
    title: 'Health Examination and TB Clearance',
    description: 'Connecticut requires physical examination including TB test before patient care per 19-13-D71(a)(5)',
    severity: 'high',
    regulationCode: '19-13-D71',
    regulationName: 'Health Examination',
    agencyName: 'Connecticut Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(860) 509-7400',
    agencyUrl: 'https://portal.ct.gov/DPH',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL direct care employees have physical examination documentation including TB test BEFORE patient assignment?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all employees have complete documentation' },
          { value: 'most', label: 'Most do, some may be incomplete' },
          { value: 'no', label: 'No / Not tracking this' },
        ],
        required: true,
        helpText: '19-13-D71(a)(5) requires physical exam including tuberculin test and statement free from communicable diseases PRIOR to patient care assignment',
      },
      {
        id: 'q2',
        question: 'Does the physical examination include physician statement that employee is free from communicable diseases?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Physical examination and TB clearance requirements are met per 19-13-D71(a)(5).',
      atRisk: 'Some employees may lack required health documentation. Review all files for TB test and physician statement.',
      nonCompliant: 'Employees are providing care without required health clearance. This violates 19-13-D71(a)(5). Immediately obtain documentation.',
      unknown: 'Verify health examination requirements per 19-13-D71(a)(5).',
    },
    stateNotes: [
      'Physical examination required PRIOR to assignment to patient care activities',
      'Must include tuberculin test',
      'Must include physician (or designee) statement that employee is free from communicable diseases',
    ],
  },

  {
    id: 'CT-B3',
    itemId: 'B3',
    stateCode: 'CT',
    title: 'Professional License Verification',
    description: 'Connecticut requires verification of professional licenses for licensed staff',
    severity: 'high',
    regulationCode: 'CGS 20-102',
    regulationName: 'License Verification',
    agencyName: 'Connecticut Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(860) 509-7400',
    agencyUrl: 'https://www.elicense.ct.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify Connecticut professional licenses for all licensed staff (RNs, LPNs) before hire?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How do you track license renewal dates?',
        inputType: 'select',
        options: [
          { value: 'system', label: 'Automated tracking system with reminders' },
          { value: 'manual', label: 'Manual tracking (spreadsheet, calendar)' },
          { value: 'none', label: 'No systematic tracking' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'includes', questionId: 'q2', value: ['system', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'none', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Professional license verification and tracking meets Connecticut requirements.',
      atRisk: 'License tracking may be inadequate. Implement systematic tracking to prevent expired licenses.',
      nonCompliant: 'Failing to verify professional licenses violates Connecticut law. Use eLicense CT to verify all licensed staff.',
      unknown: 'Verify all professional licenses at elicense.ct.gov.',
    },
    stateNotes: [
      'License verification available at elicense.ct.gov',
      'RN licenses in Connecticut renew annually by September 30',
      'Must verify license before hire and track renewal dates',
      'Connecticut is a Nurse Licensure Compact (NLC) state',
      'Multistate licenses from other NLC states valid in Connecticut',
    ],
  },

  // ============================================
  // SECTION C: BACKGROUND SCREENING
  // ============================================
  {
    id: 'CT-C1',
    itemId: 'C1',
    stateCode: 'CT',
    title: 'ABCMS Criminal Background Check (CGS 19a-491c)',
    description: 'Connecticut requires fingerprint-based background checks through ABCMS per CGS 19a-491c',
    severity: 'high',
    regulationCode: 'CGS 19a-491c',
    regulationName: 'Background Check',
    agencyName: 'Connecticut Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(860) 509-7400',
    agencyUrl: 'https://portal.ct.gov/DPH/ABCMS/LongTerm-Care-Background-Search-Program-Applicant-Background-Check-Management-System-ABCMS',
    questions: [
      {
        id: 'q1',
        question: 'Do you use the DPH Applicant Background Check Management System (ABCMS) for all new direct care employees?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all employees go through ABCMS' },
          { value: 'most', label: 'Most do, some may bypass' },
          { value: 'no', label: 'No / Not using ABCMS' },
        ],
        required: true,
        helpText: 'CGS 19a-491c requires fingerprint-based state and national criminal history checks through ABCMS',
      },
      {
        id: 'q2',
        question: 'Which background checks are completed through ABCMS? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'state', label: 'Connecticut State Police criminal check' },
          { value: 'fbi', label: 'FBI national fingerprint-based check' },
          { value: 'nurse_aide', label: 'Nurse Aide Registry (20-102bb)' },
          { value: 'abuse', label: 'Patient abuse registry' },
        ],
        helpText: 'ABCMS includes checks of criminal records per 29-17a and nurse aide registry per 20-102bb',
      },
      {
        id: 'q3',
        question: 'Are all background checks completed BEFORE employees have any patient contact?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, always before patient contact' },
          { value: 'no', label: 'No, sometimes employees start early' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if employees start before background check clearance (direct violation)
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'ABCMS background check process meets CGS 19a-491c requirements.',
      atRisk: 'ABCMS process may have gaps. Ensure ALL direct care employees complete full background check BEFORE patient contact.',
      nonCompliant: 'VIOLATION: Employees working before background check clearance OR failure to use ABCMS violates CGS 19a-491c. Per Connecticut law, NO patient contact is allowed until background check is completed. Register at portal.ct.gov/DPH/ABCMS and submit all employees.',
      unknown: 'Contact DPH ABCMS program at (860) 509-7400.',
    },
    stateNotes: [
      'ABCMS is the web-based portal for long-term care background checks',
      'Required checks: state criminal (29-17a), FBI fingerprint, nurse aide registry (20-102bb)',
      'Disqualifying offenses defined in 42 USC 1320a-7(a) and CGS 53a-59a, 53a-60b, 53a-60c, 53a-61a, 53a-321, 53a-322, 53a-323',
      'Fingerprinting available at Connecticut State Police troop locations',
      'Background check must be completed BEFORE employment in direct care role',
    ],
  },

  {
    id: 'CT-C2',
    itemId: 'C2',
    stateCode: 'CT',
    title: 'Nurse Aide Registry Verification',
    description: 'Connecticut requires verification against the Nurse Aide Registry per CGS 20-102bb',
    severity: 'high',
    regulationCode: 'CGS 20-102bb',
    regulationName: 'Nurse Aide Registry',
    agencyName: 'Connecticut Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(860) 509-7400',
    agencyUrl: 'https://portal.ct.gov/DPH',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify all potential direct care hires against the Connecticut Nurse Aide Registry before employment?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, 100% verified through ABCMS or directly' },
          { value: 'most', label: 'Most are verified, some may start before' },
          { value: 'no', label: 'No / Not sure how to check' },
        ],
        required: true,
        helpText: 'Registry lists individuals with substantiated abuse, neglect, or misappropriation findings',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All potential hires are verified against the Connecticut Nurse Aide Registry.',
      atRisk: 'Registry verification is inconsistent. All employees must be verified before hire.',
      nonCompliant: 'Failing to verify the Nurse Aide Registry violates Connecticut regulations. Immediately verify all current staff.',
      unknown: 'Contact DPH at (860) 509-7400 for registry verification procedures.',
    },
    stateNotes: [
      'Nurse Aide Registry verification required per CGS 20-102bb',
      'Part of ABCMS comprehensive background check',
      'Check for substantiated abuse, neglect, or misappropriation findings',
      'Registry verification must be documented before hire',
      'Available through ABCMS portal or direct DPH inquiry',
    ],
  },

  {
    id: 'CT-C3',
    itemId: 'C3',
    stateCode: 'CT',
    title: 'Sex Offender Registry Check',
    description: 'Connecticut requires verification against the Sex Offender Registry per CGS 54-257',
    severity: 'high',
    regulationCode: 'CGS 54-257',
    regulationName: 'Sex Offender Registry',
    agencyName: 'Connecticut Department of Emergency Services and Public Protection',
    agencyAbbr: 'DESPP',
    agencyPhone: '(860) 685-8060',
    agencyUrl: 'https://portal.ct.gov/DESPP/Division-of-State-Police/Sex-Offender-Registry',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the Connecticut Sex Offender Registry for all potential hires?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'This is included in the ABCMS comprehensive background check under CGS 19a-491c',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Sex offender registry checks are completed for all hires.',
      atRisk: 'Sex offender registry check process should be verified.',
      nonCompliant: 'Sex offender registry checks are required under CGS 54-257. This is part of ABCMS.',
      unknown: 'Verify sex offender registry check requirements with DESPP.',
    },
    stateNotes: [
      'Sex Offender Registry maintained by DESPP per CGS 54-257',
      'Part of comprehensive ABCMS background check',
      'Public registry available at portal.ct.gov/DESPP',
      'Must be checked before hire for all direct care positions',
      'Document verification date and results in personnel file',
    ],
  },

  {
    id: 'CT-C4',
    itemId: 'C4',
    stateCode: 'CT',
    title: 'DCF Abuse Registry Check',
    description: 'Connecticut requires checking DCF Central Registry for child abuse/neglect findings',
    severity: 'high',
    regulationCode: 'CGS 17a-101k',
    regulationName: 'DCF Registry',
    agencyName: 'Connecticut Department of Children and Families',
    agencyAbbr: 'DCF',
    agencyPhone: '(860) 550-6301',
    agencyUrl: 'https://portal.ct.gov/DCF',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the DCF Central Registry for substantiated abuse/neglect findings?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'DCF abuse/neglect registry checks are completed for all hires.',
      atRisk: 'DCF registry check is recommended. Contact DCF at (860) 550-6301.',
      nonCompliant: 'DCF abuse registry checks should be implemented.',
      unknown: 'Verify DCF registry check requirements.',
    },
    stateNotes: [
      'DCF Central Registry contains substantiated child abuse/neglect findings per CGS 17a-101k',
      'Check recommended for all direct care employees',
      'Registry check request forms available through DCF',
      'Results typically received within 10 business days',
      'Individuals with substantiated findings may be ineligible for hire',
    ],
  },

  {
    id: 'CT-C5',
    itemId: 'C5',
    stateCode: 'CT',
    title: 'OIG Exclusion List Verification',
    description: 'Federal requirement to check OIG List of Excluded Individuals/Entities',
    severity: 'high',
    regulationCode: '42 CFR 1001.1901',
    regulationName: 'OIG Exclusion',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the OIG exclusion list before hiring and monthly thereafter?',
        inputType: 'select',
        options: [
          { value: 'hire_monthly', label: 'Yes, before hire and monthly' },
          { value: 'hire_only', label: 'Yes, before hire only' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'hire_monthly', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG exclusion list verification is performed before hire and monthly.',
      atRisk: 'OIG exclusion list should be checked monthly, not just at hire. Implement monthly screening.',
      nonCompliant: 'OIG exclusion list checks are required for Medicare/Medicaid participation. Employing excluded individuals can result in penalties.',
      unknown: 'Check the OIG exclusion list at exclusions.oig.hhs.gov.',
    },
    stateNotes: [
      'OIG LEIE check required per 42 CFR 1001.1901 for Medicare/Medicaid providers',
      'Check before hire and monthly thereafter per OIG guidance',
      'Free searchable database at exclusions.oig.hhs.gov',
      'Employing excluded individuals can result in civil monetary penalties',
      'Document all checks with date, names checked, and results',
    ],
  },

  // ============================================
  // SECTION D: HEALTH & SAFETY
  // ============================================
  {
    id: 'CT-D1',
    itemId: 'D1',
    stateCode: 'CT',
    title: 'Infection Control Policies and Training',
    description: 'Connecticut requires comprehensive infection control policies and training',
    severity: 'high',
    regulationCode: '19-13-D72',
    regulationName: 'Infection Control',
    agencyName: 'Connecticut Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(860) 509-7400',
    agencyUrl: 'https://portal.ct.gov/DPH',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have documented infection control policies?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do all staff receive infection control training?',
        inputType: 'select',
        options: [
          { value: 'initial_annual', label: 'Yes, during orientation and annually' },
          { value: 'initial_only', label: 'Yes, during orientation only' },
          { value: 'no', label: 'No formal training' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'initial_annual' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'initial_only', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control policies and training meet Connecticut requirements.',
      atRisk: 'Infection control training should be provided annually. Update your training program.',
      nonCompliant: 'Infection control policies are required. Develop policies immediately.',
      unknown: 'Review infection control requirements.',
    },
    stateNotes: [
      'Infection control policies required per 19-13-D72',
      'Training must include standard precautions and hand hygiene',
      'Annual training updates recommended',
      'Must address bloodborne pathogens and respiratory infections',
      'PPE availability and proper use must be addressed',
    ],
  },

  {
    id: 'CT-D2',
    itemId: 'D2',
    stateCode: 'CT',
    title: 'Bloodborne Pathogens / Hepatitis B',
    description: 'OSHA requires offering Hepatitis B vaccination to employees with occupational exposure',
    severity: 'medium',
    regulationCode: '29 CFR 1910.1030',
    regulationName: 'Bloodborne Pathogens',
    agencyName: 'Occupational Safety and Health Administration',
    agencyAbbr: 'OSHA',
    agencyPhone: '(800) 321-6742',
    agencyUrl: 'https://www.osha.gov/bloodborne-pathogens',
    questions: [
      {
        id: 'q1',
        question: 'Do you offer Hepatitis B vaccination to all employees with potential blood exposure?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you document vaccination acceptance or declination?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Hepatitis B vaccination program meets OSHA requirements.',
      atRisk: 'Hepatitis B vaccination offer should be documented. Obtain signed acceptance or declination forms.',
      nonCompliant: 'OSHA requires offering Hepatitis B vaccination to employees with occupational exposure. Implement immediately.',
      unknown: 'Review OSHA bloodborne pathogens standard requirements.',
    },
    stateNotes: [
      'Hepatitis B vaccination required per OSHA 29 CFR 1910.1030',
      'Must be offered within 10 days of employment for at-risk employees',
      'Vaccination is free to employees under OSHA standards',
      'Declination must be documented using OSHA-compliant form',
      'Employees may change declination at any time at no cost',
    ],
  },

  {
    id: 'CT-D3',
    itemId: 'D3',
    stateCode: 'CT',
    title: 'Workplace Safety Program',
    description: 'OSHA requires workplace safety programs for all employers',
    severity: 'medium',
    regulationCode: '29 CFR 1904',
    regulationName: 'Workplace Safety',
    agencyName: 'Occupational Safety and Health Administration',
    agencyAbbr: 'OSHA',
    agencyPhone: '(800) 321-6742',
    agencyUrl: 'https://www.osha.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written workplace safety program?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you maintain OSHA 300 logs for workplace injuries?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Workplace safety program meets OSHA requirements.',
      atRisk: 'OSHA 300 log maintenance may be incomplete. Ensure all workplace injuries are properly recorded.',
      nonCompliant: 'OSHA requires a workplace safety program. Develop and implement immediately.',
      unknown: 'Review OSHA workplace safety requirements.',
    },
    stateNotes: [
      'OSHA 300 log required per 29 CFR 1904 for employers with 10+ employees',
      'Log must be maintained and posted annually from Feb 1 to April 30',
      'Workplace injuries must be recorded within 7 days',
      'Connecticut OSHA Plan operates under federal OSHA authority',
      'Contact CONN-OSHA at (860) 263-6900 for state-specific guidance',
    ],
  },

  // ============================================
  // SECTION E: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'CT-E1',
    itemId: 'E1',
    stateCode: 'CT',
    title: 'Homemaker-Home Health Aide Initial Training (75 Hours)',
    description: 'Connecticut requires 75 hours initial training per 19-13-D69(d)(2)',
    severity: 'high',
    regulationCode: '19-13-D69',
    regulationName: 'Initial Training',
    agencyName: 'Connecticut Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(860) 509-7400',
    agencyUrl: 'https://portal.ct.gov/DPH',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL new homemaker-home health aides complete the following training requirements?',
        inputType: 'checklist',
        options: [
          { value: '75_hours', label: '75 hours total training (lecture and clinical)' },
          { value: 'communication', label: 'Communication skills' },
          { value: 'observation', label: 'Observation, reporting and documentation of patient status' },
          { value: 'vitals', label: 'Reading and recording temperature, pulse and respiration' },
          { value: 'infection', label: 'Basic infection control procedures' },
          { value: 'body_function', label: 'Basic elements of body function and changes to report' },
          { value: 'environment', label: 'Maintenance of clean, safe and healthy environment' },
          { value: 'emergency', label: 'Recognizing emergencies and emergency procedures' },
          { value: 'hygiene', label: 'Personal hygiene and grooming (bathing, skin care, toileting)' },
          { value: 'transfer', label: 'Safe transfer techniques and ambulation' },
          { value: 'competency', label: 'Competency evaluation upon completion' },
        ],
        required: true,
        helpText: '19-13-D69(d)(2)(A) specifies the required curriculum elements',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Training program meets Connecticut 19-13-D69(d)(2) requirements (75 hours with all required elements).',
      atRisk: 'Training program may be incomplete. 19-13-D69(d)(2) requires 75 hours with specific curriculum elements.',
      nonCompliant: 'Aides are providing services without required training. This violates 19-13-D69(d)(2). Immediately complete training.',
      unknown: 'Review training program against 19-13-D69(d)(2) requirements.',
    },
    stateNotes: [
      'Training must be not less than 75 hours per 19-13-D69(d)(2)(A)',
      'Must include both lecture and clinical settings',
      'Curriculum elements are specified in 19-13-D69(d)(2)(A)(i) through (xiii)',
      'Competency evaluation required upon completion',
      'DPH commissioner adopts and may revise the program',
    ],
  },

  {
    id: 'CT-E2',
    itemId: 'E2',
    stateCode: 'CT',
    title: 'Orientation Training (10 Hours)',
    description: 'Connecticut requires 10 hours orientation before patient contact per 19-13-D66 et seq.',
    severity: 'high',
    regulationCode: '19-13-D71',
    regulationName: 'Orientation',
    agencyName: 'Connecticut Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(860) 509-7400',
    agencyUrl: 'https://portal.ct.gov/DPH',
    questions: [
      {
        id: 'q1',
        question: 'Do all new hires complete orientation training BEFORE their first patient assignment?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: '10 hours of orientation required before providing services',
      },
      {
        id: 'q2',
        question: 'Which topics does your orientation include? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'policies', label: 'Agency policies and procedures' },
          { value: 'emergency', label: 'Emergency procedures' },
          { value: 'hipaa', label: 'HIPAA and patient privacy' },
          { value: 'abuse', label: 'Abuse recognition and mandatory reporting' },
          { value: 'documentation', label: 'Documentation requirements' },
          { value: 'infection', label: 'Infection control' },
          { value: 'rights', label: 'Patient rights' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 6 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Orientation training meets Connecticut requirements.',
      atRisk: 'Orientation training may be incomplete. Review required topics per 19-13-D71.',
      nonCompliant: 'Staff are working before completing orientation. This violates Connecticut regulations.',
      unknown: 'Review orientation training requirements.',
    },
    stateNotes: [
      '10 hours orientation required before providing services per 19-13-D66 et seq.',
      'Must be completed BEFORE first patient assignment',
      'Topics must include agency policies, emergency procedures, and patient rights',
      'HIPAA training should be included in orientation',
      'Document orientation with dates, topics, and employee signature',
    ],
  },

  {
    id: 'CT-E3',
    itemId: 'E3',
    stateCode: 'CT',
    title: 'Annual In-Service Training (12 Hours)',
    description: 'Connecticut requires 12 hours of annual continuing education per 19-13-D69(d)(2)',
    severity: 'high',
    regulationCode: '19-13-D69',
    regulationName: 'Annual Training',
    agencyName: 'Connecticut Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(860) 509-7400',
    agencyUrl: 'https://portal.ct.gov/DPH',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL homemaker-home health aides complete 12 hours of annual in-service training?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all aides complete 12+ hours annually' },
          { value: 'most', label: 'Most complete required hours, some are behind' },
          { value: 'no', label: 'No structured annual training program' },
        ],
        required: true,
        helpText: 'Federal regulations require 12 hours of in-service training per 12-month period',
      },
      {
        id: 'q2',
        question: 'Which topics does your annual training cover? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'patient_care', label: 'Patient care techniques' },
          { value: 'safety', label: 'Safety procedures' },
          { value: 'infection', label: 'Infection control updates' },
          { value: 'communication', label: 'Communication skills' },
          { value: 'abuse', label: 'Abuse recognition and reporting' },
          { value: 'emergency', label: 'Emergency procedures' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Annual training program meets Connecticut and federal requirements (12 hours).',
      atRisk: 'Some aides may be behind on annual training. Review records and schedule catch-up training.',
      nonCompliant: 'No structured annual training program exists. Implement a 12-hour annual training program immediately.',
      unknown: 'Review annual training requirements.',
    },
    stateNotes: [
      '12 hours annual in-service training required per 19-13-D69(d)(2)',
      'Must be completed each 12-month period from hire date',
      'Training topics should be based on agency needs and quality assurance findings',
      'Federal Medicare CoPs also require 12 hours annually',
      'Document training with dates, topics, hours, and employee signature',
    ],
  },

  {
    id: 'CT-E4',
    itemId: 'E4',
    stateCode: 'CT',
    title: 'Competency Evaluation',
    description: 'Connecticut requires competency evaluation for homemaker-home health aides per 19-13-D69(d)(2)',
    severity: 'high',
    regulationCode: '19-13-D69',
    regulationName: 'Competency',
    agencyName: 'Connecticut Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(860) 509-7400',
    agencyUrl: 'https://portal.ct.gov/DPH',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct competency evaluations for all homemaker-home health aides?',
        inputType: 'select',
        options: [
          { value: 'initial_ongoing', label: 'Yes, upon training completion and ongoing' },
          { value: 'initial_only', label: 'Yes, upon training completion only' },
          { value: 'no', label: 'No formal competency evaluation' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which skills are evaluated? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'personal_care', label: 'Personal care skills' },
          { value: 'vital_signs', label: 'Vital signs measurement' },
          { value: 'safety', label: 'Safety and emergency response' },
          { value: 'communication', label: 'Communication skills' },
          { value: 'documentation', label: 'Documentation accuracy' },
          { value: 'infection', label: 'Infection control practices' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'initial_ongoing' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'initial_only', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Competency evaluation program meets Connecticut requirements per 19-13-D69(d)(2).',
      atRisk: 'Competency evaluations should be conducted initially and ongoing. Consider annual competency checks.',
      nonCompliant: 'Competency evaluations are required per 19-13-D69(d)(2). Implement immediately.',
      unknown: 'Review competency evaluation requirements.',
    },
    stateNotes: [
      'Competency evaluation required upon training completion per 19-13-D69(d)(2)',
      'Must cover all skills taught in 75-hour training program',
      'RN or qualified instructor must conduct evaluation',
      'Failed competencies must be remediated before patient care',
      'Annual competency evaluations recommended for quality assurance',
    ],
  },

  {
    id: 'CT-E5',
    itemId: 'E5',
    stateCode: 'CT',
    title: 'Mandatory Reporter Training',
    description: 'Connecticut requires training on mandatory abuse reporting per CGS 17b-451',
    severity: 'high',
    regulationCode: 'CGS 17b-451',
    regulationName: 'Mandatory Reporting',
    agencyName: 'Connecticut Department of Social Services',
    agencyAbbr: 'DSS',
    agencyPhone: '(888) 385-4225',
    agencyUrl: 'https://portal.ct.gov/DSS',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your homemaker-home health aides receive training on mandatory abuse reporting under Connecticut law?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all aides receive abuse reporting training' },
          { value: 'most', label: 'Most aides receive training' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
        helpText: 'Connecticut law requires reporting suspected abuse of elderly persons to DSS',
      },
      {
        id: 'q2',
        question: 'Do staff know how to report suspected abuse?',
        inputType: 'select',
        options: [
          { value: 'orientation_annual', label: 'Yes, trained during orientation and annually' },
          { value: 'orientation_only', label: 'Yes, trained during orientation only' },
          { value: 'no', label: 'No formal training on reporting procedures' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'orientation_annual' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'orientation_only' },
      ], result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All aides receive abuse reporting training per CGS 17b-451.',
      atRisk: 'Abuse reporting training should be provided during orientation and annually. Update your training program.',
      nonCompliant: 'Failure to train staff on mandatory reporting violates Connecticut law. Staff who fail to report can face criminal penalties. Implement training immediately.',
      unknown: 'Verify all aides receive abuse reporting training per CGS 17b-451.',
    },
    stateNotes: [
      'Mandatory reporters under CGS 17b-451 include home health aides',
      'Must report suspected abuse, neglect, or exploitation of elderly persons (60+)',
      'Report to DSS Elder Abuse Hotline: 1-888-385-4225 (business hours) or 2-1-1 (after hours)',
      'Reports must be made within 24 hours (PA 22-145, effective 7/1/22)',
      'Failure to report within 24 hours: up to $500 fine + mandatory retraining',
      'Intentional failure to report: Class C misdemeanor (1st offense), Class A misdemeanor (subsequent)',
      'Reporters are immune from civil/criminal liability for good faith reports',
    ],
  },

  {
    id: 'CT-E6',
    itemId: 'E6',
    stateCode: 'CT',
    title: 'HIPAA Training',
    description: 'Federal HIPAA regulations require privacy and security training',
    severity: 'high',
    regulationCode: '45 CFR 164.530',
    regulationName: 'HIPAA Training',
    agencyName: 'U.S. Department of Health and Human Services',
    agencyAbbr: 'HHS OCR',
    agencyPhone: '(800) 368-1019',
    agencyUrl: 'https://www.hhs.gov/hipaa',
    questions: [
      {
        id: 'q1',
        question: 'Do all staff receive HIPAA privacy and security training?',
        inputType: 'select',
        options: [
          { value: 'initial_annual', label: 'Yes, during orientation and annually' },
          { value: 'initial_only', label: 'Yes, during orientation only' },
          { value: 'no', label: 'No formal HIPAA training' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you document HIPAA training completion?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'initial_annual' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'initial_only', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HIPAA training program meets federal requirements per 45 CFR 164.530.',
      atRisk: 'HIPAA training should be provided annually and documented. Update your training program.',
      nonCompliant: 'HIPAA training is required for all staff. Failure to train can result in OCR penalties. Implement immediately.',
      unknown: 'Review HIPAA training requirements at hhs.gov/hipaa.',
    },
    stateNotes: [
      'HIPAA training required per 45 CFR 164.530(b)(1)',
      'Must be provided during orientation and annually thereafter',
      'Training must cover Privacy Rule and Security Rule requirements',
      'Document training completion with date and employee signature',
      'Non-compliance penalties can range from $100 to $50,000+ per violation',
    ],
  },

  // ============================================
  // SECTION F: CARE DELIVERY
  // ============================================
  {
    id: 'CT-F1',
    itemId: 'F1',
    stateCode: 'CT',
    title: 'Patient Care Plans',
    description: 'Written care plans required for each patient per 19-13-D73',
    severity: 'high',
    regulationCode: '19-13-D73',
    regulationName: 'Care Plan',
    agencyName: 'Connecticut Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(860) 509-7400',
    agencyUrl: 'https://portal.ct.gov/DPH',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written care plan for EVERY patient before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your care plans include all required elements?',
        inputType: 'checklist',
        options: [
          { value: 'services', label: 'Specific services to be provided' },
          { value: 'frequency', label: 'Frequency and duration of services' },
          { value: 'goals', label: 'Patient goals and outcomes' },
          { value: 'supervision', label: 'RN supervision schedule' },
          { value: 'emergency', label: 'Emergency contact information' },
          { value: 'signature', label: 'Patient/representative signature' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'always',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Care plans are completed before services begin and include all required elements per 19-13-D73.',
      atRisk: 'Care plans may be incomplete or created after services begin. 19-13-D73 requires written plans before first service.',
      nonCompliant: 'Services are being provided without written care plans. This violates 19-13-D73. Immediately create care plans for all patients.',
      unknown: 'Review care plan requirements per 19-13-D73.',
    },
    stateNotes: [
      'Written care plan required per 19-13-D73 before services begin',
      'Plan must include services, frequency, duration, and patient goals',
      'RN supervision schedule must be included in care plan',
      'Patient or representative signature required',
      'Care plans must be reviewed and updated as patient needs change',
    ],
  },

  {
    id: 'CT-F2',
    itemId: 'F2',
    stateCode: 'CT',
    title: 'Electronic Visit Verification (EVV)',
    description: 'EVV required for Medicaid-funded personal care services per 21st Century Cures Act',
    severity: 'high',
    regulationCode: '21st Century Cures Act',
    regulationName: 'EVV',
    agencyName: 'Connecticut Department of Social Services',
    agencyAbbr: 'DSS',
    agencyPhone: '(860) 424-5008',
    agencyUrl: 'https://portal.ct.gov/DSS/Health-And-Home-Care/EVV',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide Medicaid-funded personal care services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - private pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you use a DSS-approved EVV system to document visits?',
        inputType: 'select',
        options: [
          { value: 'yes_compliant', label: 'Yes, fully implemented and compliant' },
          { value: 'yes_partial', label: 'Yes, but not for all visits' },
          { value: 'no', label: 'No EVV system in place' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes_compliant', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes_partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'EVV system is properly implemented for Medicaid-funded services.',
      atRisk: 'EVV is not being used consistently for all Medicaid visits. DSS requires EVV for all personal care services. Ensure 100% EVV compliance.',
      nonCompliant: 'Medicaid personal care services require EVV per federal law. Failure to implement EVV can result in claim denials. Contact DSS at (860) 424-5008.',
      unknown: 'If you provide Medicaid services, verify EVV compliance with DSS requirements.',
    },
    stateNotes: [
      'EVV required under 21st Century Cures Act for Medicaid personal care services',
      'Connecticut DSS oversees EVV implementation',
      'Must capture: type of service, date, location, provider, and start/end times',
      'Non-compliance can result in claim denials and Medicaid sanctions',
      'Contact DSS at (860) 424-5008 for EVV vendor information',
    ],
  },

  {
    id: 'CT-F3',
    itemId: 'F3',
    stateCode: 'CT',
    title: 'Service Documentation',
    description: 'Connecticut requires documentation of all services provided',
    severity: 'high',
    regulationCode: '19-13-D75',
    regulationName: 'Documentation',
    agencyName: 'Connecticut Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(860) 509-7400',
    agencyUrl: 'https://portal.ct.gov/DPH',
    questions: [
      {
        id: 'q1',
        question: 'Do aides document services provided at each visit?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'What information is captured in visit documentation?',
        inputType: 'checklist',
        options: [
          { value: 'time', label: 'Arrival and departure times' },
          { value: 'services', label: 'Services performed' },
          { value: 'observations', label: 'Patient observations' },
          { value: 'signature', label: 'Aide signature' },
          { value: 'patient_sig', label: 'Patient or representative signature' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Service documentation meets Connecticut requirements per 19-13-D75.',
      atRisk: 'Visit documentation may be incomplete. Ensure all required elements are captured.',
      nonCompliant: 'Services must be documented at each visit. Implement documentation process immediately.',
      unknown: 'Review service documentation requirements.',
    },
    stateNotes: [
      'Service documentation required per 19-13-D75',
      'Must include date, arrival/departure times, and services performed',
      'Patient observations and changes in condition must be documented',
      'Aide signature required for each visit',
      'Patient or representative signature recommended for verification',
    ],
  },

  {
    id: 'CT-F4',
    itemId: 'F4',
    stateCode: 'CT',
    title: 'Emergency Preparedness',
    description: 'Connecticut requires emergency preparedness plans',
    severity: 'medium',
    regulationCode: '19-13-D72',
    regulationName: 'Emergency Preparedness',
    agencyName: 'Connecticut Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(860) 509-7400',
    agencyUrl: 'https://portal.ct.gov/DPH',
    questions: [
      {
        id: 'q1',
        question: 'Do you have written emergency preparedness policies?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which emergency situations are addressed in your plan?',
        inputType: 'checklist',
        options: [
          { value: 'weather', label: 'Severe weather/natural disasters' },
          { value: 'pandemic', label: 'Pandemic/disease outbreaks' },
          { value: 'patient_emergency', label: 'Patient medical emergencies' },
          { value: 'communication', label: 'Communication backup systems' },
          { value: 'staff_safety', label: 'Staff safety protocols' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness plan meets Connecticut requirements.',
      atRisk: 'Emergency preparedness plan may be incomplete. Consider adding additional scenarios.',
      nonCompliant: 'Emergency preparedness policies are required. Develop a comprehensive plan immediately.',
      unknown: 'Review emergency preparedness requirements.',
    },
    stateNotes: [
      'Emergency preparedness required per 19-13-D72',
      'Plan must address severe weather, pandemics, and patient emergencies',
      'Staff must know emergency procedures and contact information',
      'Communication backup plans required for contacting patients and staff',
      'Annual review and testing of emergency procedures recommended',
    ],
  },

  // ============================================
  // SECTION G: DOCUMENTATION/ADMIN
  // ============================================
  {
    id: 'CT-G1',
    itemId: 'G1',
    stateCode: 'CT',
    title: 'Document Expiration Tracking',
    description: 'System to track license, certification, and clearance expirations',
    severity: 'high',
    regulationCode: '19-13-D71',
    regulationName: 'Record Maintenance',
    agencyName: 'Connecticut Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(860) 509-7400',
    agencyUrl: 'https://portal.ct.gov/DPH',
    questions: [
      {
        id: 'q1',
        question: 'Do you track expiration dates for all required documents?',
        inputType: 'checklist',
        options: [
          { value: 'agency_license', label: 'Agency license' },
          { value: 'insurance', label: 'Liability insurance' },
          { value: 'tb', label: 'TB clearances' },
          { value: 'cpr', label: 'CPR/First Aid certifications' },
          { value: 'professional', label: 'Professional licenses (RN, LPN)' },
          { value: 'training', label: 'Annual training due dates' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you receive advance reminders before documents expire?',
        inputType: 'select',
        options: [
          { value: 'auto', label: 'Yes, automated reminders 30+ days in advance' },
          { value: 'manual', label: 'Yes, manual review monthly' },
          { value: 'no', label: 'No proactive tracking' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'includes', questionId: 'q2', value: ['auto', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Document expiration tracking is comprehensive with proactive reminders.',
      atRisk: 'Expiration tracking is incomplete. Implement tracking to prevent compliance lapses.',
      nonCompliant: 'No systematic document tracking in place. This creates significant compliance risk.',
      unknown: 'Implement document expiration tracking system.',
    },
    stateNotes: [
      'Record maintenance required per 19-13-D71',
      'Track agency license, insurance, and professional licenses',
      'Employee TB clearances and CPR certifications must be current',
      'Annual training due dates should be tracked proactively',
      '30-60 day advance reminders recommended to prevent lapses',
    ],
  },

  {
    id: 'CT-G2',
    itemId: 'G2',
    stateCode: 'CT',
    title: 'HIPAA Documentation',
    description: 'Federal HIPAA requires specific privacy and security documentation',
    severity: 'high',
    regulationCode: '45 CFR 164',
    regulationName: 'HIPAA',
    agencyName: 'U.S. Department of Health and Human Services',
    agencyAbbr: 'HHS OCR',
    agencyPhone: '(800) 368-1019',
    agencyUrl: 'https://www.hhs.gov/hipaa',
    questions: [
      {
        id: 'q1',
        question: 'Do you have required HIPAA documentation?',
        inputType: 'checklist',
        options: [
          { value: 'npp', label: 'Notice of Privacy Practices' },
          { value: 'policies', label: 'Written privacy policies' },
          { value: 'security', label: 'Security policies and procedures' },
          { value: 'baa', label: 'Business Associate Agreements with vendors' },
          { value: 'training', label: 'Training documentation' },
          { value: 'breach', label: 'Breach notification procedures' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HIPAA documentation meets federal requirements.',
      atRisk: 'HIPAA documentation may be incomplete. Review 45 CFR 164 requirements.',
      nonCompliant: 'Critical HIPAA documentation is missing. Non-compliance can result in OCR penalties. Implement immediately.',
      unknown: 'Review HIPAA documentation requirements at hhs.gov/hipaa.',
    },
    stateNotes: [
      'HIPAA documentation required per 45 CFR 164',
      'Notice of Privacy Practices must be provided to all patients',
      'Business Associate Agreements required with all vendors handling PHI',
      'Security policies must address administrative, physical, and technical safeguards',
      'Breach notification procedures required per 45 CFR 164.400-414',
    ],
  },

  {
    id: 'CT-G3',
    itemId: 'G3',
    stateCode: 'CT',
    title: 'Audit-Ready Documentation',
    description: 'Personnel files organized for DPH surveyor review',
    severity: 'medium',
    regulationCode: '19-13-D77',
    regulationName: 'Audit Readiness',
    agencyName: 'Connecticut Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(860) 509-7400',
    agencyUrl: 'https://portal.ct.gov/DPH',
    questions: [
      {
        id: 'q1',
        question: 'Are your personnel files organized and accessible for DPH inspection?',
        inputType: 'select',
        options: [
          { value: 'organized', label: 'Yes, all files are organized with consistent structure' },
          { value: 'mostly', label: 'Most files are organized, some need work' },
          { value: 'disorganized', label: 'Files are not well organized' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Could you produce any aide\'s complete personnel file within 5 minutes if DPH arrived for an inspection?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'organized' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'mostly', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'disorganized', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files are well-organized and ready for DPH inspection.',
      atRisk: 'File organization needs improvement. DPH surveyors expect immediate access to complete personnel files.',
      nonCompliant: 'Disorganized files will result in survey deficiencies. Implement a standardized file structure immediately.',
      unknown: 'Review file organization against DPH survey requirements.',
    },
    stateNotes: [
      'Administrative records required per 19-13-D77',
      'DPH may conduct announced or unannounced inspections',
      'Files should be organized with consistent structure for each employee',
      'Use checklist to ensure all required documents are present',
      'Electronic files acceptable if accessible and searchable',
    ],
  },

  {
    id: 'CT-G4',
    itemId: 'G4',
    stateCode: 'CT',
    title: 'Workers Compensation Documentation',
    description: 'Connecticut requires workers compensation coverage documentation',
    severity: 'medium',
    regulationCode: 'CGS 31-284',
    regulationName: 'Workers Compensation',
    agencyName: 'Connecticut Workers Compensation Commission',
    agencyAbbr: 'WCC',
    agencyPhone: '(860) 493-1500',
    agencyUrl: 'https://wcc.state.ct.us/',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain current workers compensation insurance?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you have documented procedures for reporting workplace injuries?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Workers compensation documentation meets Connecticut requirements.',
      atRisk: 'Injury reporting procedures should be documented. Review CGS 31-284 requirements.',
      nonCompliant: 'Workers compensation insurance is required per CGS 31-284. Obtain coverage immediately.',
      unknown: 'Contact WCC at (860) 493-1500 for workers compensation requirements.',
    },
    stateNotes: [
      'Workers compensation insurance required per CGS 31-284',
      'All employers with one or more employees must carry coverage',
      'Workplace injuries must be reported to WCC within specified timeframes',
      'Form 30C (First Report of Injury) required for all workplace injuries',
      'WCC contact: (860) 493-1500 or wcc.state.ct.us',
    ],
  },
];

export default CONNECTICUT_QUESTIONS;